import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Accordion, Card } from 'react-bootstrap';
import { addProductIdentifier, fetchProductIdentifier, getBrandDetails } from '../Store/SystemMasters.action';
// eslint-disable-next-line import/no-cycle
import CardContent from './CardContent';
import { Context } from './ContextWrapper';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';

function ProductIdentifierMaster() {
  const { addNewItem, cards, updateSubmitState, formState, MasterAddEditAccess, openCard, setOpenCard, setFormState, OpenReArrangeFunction } =
    useContext(Context);
  const dispatch = useDispatch();
  const [showToaster, setShowToaster] = useState(false);
  const [toasterShown, setToasterShown] = useState(false);
  const { t } = useTranslation('common');

  useEffect(() => {
    dispatch(getBrandDetails({ accessedFrom: 'masters' }));
  }, [dispatch]);

  const switchCardState = () => {
    setOpenCard((prev) => !prev);
  };

  const validateAllFields = useCallback(() => {
    let isValid = true;
    const newErrors = {};
    const newBrandErrors = {};

    // Validate each card
    cards.forEach((card) => {
      // Validate product type (name)
      if (!card.name || card.name.trim().length === 0) {
        newErrors[card._id] = t('PRODUCT_IDENTIFIER.ENTER_PRODUCT_IDENTIFIER');
        isValid = false;
      } else if (cards.some((other) => other.name.trim().toLowerCase() === card.name.trim().toLowerCase() && other._id !== card._id)) {
        newErrors[card._id] = t('PRODUCT_IDENTIFIER.ENTRY_ALREADY_EXIST');
        isValid = false;
      }
      // Validate brand selection
      if ((!card.brandIds || card.brandIds.length === 0) && card.isEditable) {
        newBrandErrors[card._id] = t('PRODUCT_IDENTIFIER.BRAND_REQUIRED');
        isValid = false;
      }
    });

    setFormState((prev) => ({
      ...prev,
      errors: newErrors,
      brandErrors: newBrandErrors,
      isSubmitted: true,
    }));

    return isValid;
  }, [cards, t, setFormState]);

  const handleClick = async () => {
    try {
      setFormState((prev) => ({ ...prev, isLoading: true }));

      // Validate all fields before submission
      const isValid = validateAllFields();
      if (!isValid) {
        setFormState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      // Prepare data for submission
      const updatedCards = cards.map((obj, index) => ({
        ...obj,
        ...(obj._id.includes('_') && { _id: '', seqNumber: index }),
      }));

      const objData = {
        names: JSON.stringify(updatedCards),
        section: 'masters',
        module: 'productIdentifier',
        action: 'addEdit',
      };
      // Submit the form
      await dispatch(addProductIdentifier(objData, t));
      await dispatch(fetchProductIdentifier({}, t));

      // Reset form state
      updateSubmitState(false);
      setFormState((prev) => ({
        ...prev,
        isLoading: false,
        values: {},
        errors: {},
        brandErrors: {},
      }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in handleClick:', error);
      setFormState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    if (cards?.length > 22 && !toasterShown) {
      setShowToaster(true);
      setToasterShown(true); // Set toasterShown to true to indicate that toaster has been shown
    } else if (cards?.length <= 22 && toasterShown) {
      setToasterShown(false); // Reset toasterShown to false
    } else {
      setShowToaster(false);
    }
  }, [cards?.length, toasterShown]);

  return (
    <>
      <Accordion className="mb-4 master-accordian product-identifier-master">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>
                {' '}
                <p>{t('PRODUCT_IDENTIFIER.IDENTIFIER_TYPE_TITLE')}</p>
              </p>
            </div>
            <div className="action-title-btns">
              {MasterAddEditAccess && (
                <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.REARRANGE_TOOLTIP')}>
                  <button
                    type="button"
                    onClick={() => OpenReArrangeFunction('productIdentifier')}
                    className="btn d-inline-flex mr-16 p-0"
                    disabled={formState?.isLoading || formState?.disableBtn}
                    aria-label="Rearrange"
                  >
                    <span className="icon-Rearrange text-grey  f-24" />
                  </button>
                </CustomTooltip>
              )}

              <CustomTooltip tooltipText={openCard ? t('CUSTOM_ATTRIBUTES.CLOSE') : t('CUSTOM_ATTRIBUTES.OPEN')}>
                <button className="btn d-inline-flex p-0" onClick={switchCardState} type="button" disabled={false}>
                  {openCard ? <span className="icon-new-down-arrow-icon icon-inverse f-24" /> : <span className="icon-new-down-arrow-icon f-24" />}
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {openCard ? (
            <Card.Body>
              {/*
                The Automation / Manual radio pair is hidden temporarily: every tenant now generates
                identifiers automatically, decided server-side by PRODUCT_IDENTIFIER_AUTOMATIC_ONLY
                in server/domain/product-identifier/feature-flags.ts. Restoring the choice means
                turning that constant off and bringing this block back — the toggle's endpoint stays
                refused while it is on, because switching type clears every product's identifiers.
              */}
              <CardContent />

              {MasterAddEditAccess && (
                <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_TOOLTIP')}>
                  <button
                    type="button"
                    onClick={addNewItem}
                    className="btn btn-outline ml-auto  mt-16 "
                    disabled={cards?.length >= 22 || formState?.isLoading || formState?.disableBtn}
                    aria-label="Add"
                  >
                    <span className="icon-Add text-grey  f-24" />
                  </button>
                </CustomTooltip>
              )}
              {MasterAddEditAccess && (
                <div className="section-custom-save">
                  <button className="btn" aria-label="Save" type="submit" onClick={handleClick}>
                    <i className="icon-Tick-1 f-24" />
                  </button>
                </div>
              )}
            </Card.Body>
          ) : null}
        </Card>
      </Accordion>
      {showToaster && <div>{toasterMsg(t('PRODUCT_IDENTIFIERS.IDENTIFIER_MESSAGE'), t('PRODUCT_IDENTIFIERS.DANGER'))}</div>}
    </>
  );
}

export default ProductIdentifierMaster;
